import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardCheck, Plus, Trash2, Save, X, Pencil, ClipboardList, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { course_id: '', title: '', description: '', criteria: '', pass_score: 8.0, is_active: true };

const fmtDate = (d) => d ? new Date(d).toLocaleString('pt-BR') : '';

const SubmissionsSection = () => {
  const [data, setData] = useState({ submissions: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/deliverable-submissions`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const st = data.stats || {};
  return (
    <div className="mt-10" data-testid="admin-submissions">
      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-3"><ClipboardList className="text-fuchsia-400" size={18} />Entregas Corrigidas pela IA</h3>
      {loading ? <p className="text-gray-400">Carregando...</p> : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[['total', 'Entregas', 'text-white'], ['approved', 'Aprovadas', 'text-green-400'], ['reproved', 'Reprovadas', 'text-red-400'], ['avg_score', 'Nota média', 'text-amber-400']].map(([k, l, c]) => (
              <div key={k} className="bg-black/40 border border-zinc-800 rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${c}`}>{st[k] ?? 0}</div>
                <div className="text-xs text-gray-500">{l}</div>
              </div>
            ))}
          </div>
          {data.submissions.length === 0 ? <p className="text-gray-500 text-sm">Nenhuma entrega corrigida ainda.</p> : (
            <div className="overflow-x-auto border border-zinc-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900"><tr className="text-left text-gray-400">
                  <th className="py-2 px-3">Aluno</th><th className="py-2 px-3">Avaliação</th><th className="py-2 px-3">Nota</th><th className="py-2 px-3">Data</th><th className="py-2 px-3"></th>
                </tr></thead>
                <tbody>
                  {data.submissions.map(s => (
                    <tr key={s.submission_id} className="border-t border-zinc-800/60 text-gray-200" data-testid={`submission-row-${s.submission_id}`}>
                      <td className="py-2 px-3"><div>{s.student_name}</div><div className="text-gray-500 text-xs">{s.student_email}</div></td>
                      <td className="py-2 px-3">{s.assessment_title}<div className="text-gray-500 text-xs">{s.course_title}</div></td>
                      <td className="py-2 px-3"><span className={`font-bold ${s.passed ? 'text-green-400' : 'text-red-400'}`}>{s.score} {s.passed ? '✓' : '✗'}</span></td>
                      <td className="py-2 px-3 text-gray-400 text-xs">{fmtDate(s.submitted_at)}</td>
                      <td className="py-2 px-3"><Button size="sm" variant="outline" onClick={() => setView(s)} data-testid={`view-submission-${s.submission_id}`}><Eye size={14} /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Dialog open={!!view} onOpenChange={() => setView(null)}>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{view?.assessment_title} — {view?.student_name}</DialogTitle></DialogHeader>
              {view && (
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-400">Nota:</span> <span className={view.passed ? 'text-green-400' : 'text-red-400'}>{view.score} ({view.passed ? 'Aprovado' : 'Reprovado'})</span></p>
                  {view.attachment_name && <p className="text-gray-400">Anexo: {view.attachment_name}</p>}
                  <p className="text-gray-300 whitespace-pre-wrap">{view.feedback}</p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

const AdminDeliverables = () => {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // assessment_id or 'new'
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/deliverables`, { withCredentials: true }),
      axios.get(`${API}/admin/courses-basic`, { withCredentials: true }),
    ]).then(([d, c]) => { setItems(d.data); setCourses(c.data); })
      .catch(() => toast.error('Erro ao carregar')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing('new'); };
  const startEdit = (a) => {
    setForm({ course_id: a.course_id, title: a.title, description: a.description || '', criteria: (a.criteria || []).join('\n'), pass_score: a.pass_score || 8.0, is_active: a.is_active !== false });
    setEditing(a.assessment_id);
  };

  const save = async () => {
    if (!form.course_id || !form.title.trim()) { toast.error('Curso e título são obrigatórios'); return; }
    const payload = {
      course_id: parseInt(form.course_id),
      title: form.title, description: form.description,
      criteria: form.criteria.split('\n').map(s => s.trim()).filter(Boolean),
      pass_score: parseFloat(form.pass_score) || 8.0, is_active: form.is_active,
    };
    try {
      if (editing === 'new') await axios.post(`${API}/admin/deliverables`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/deliverables/${editing}`, payload, { withCredentials: true });
      toast.success('Avaliação salva'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta avaliação?')) return;
    try { await axios.delete(`${API}/admin/deliverables/${id}`, { withCredentials: true }); toast.success('Excluída'); load(); }
    catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div data-testid="admin-deliverables">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2"><ClipboardCheck className="text-red-500" size={20} />Avaliações de Entrega (corrigidas por IA)</h3>
          <p className="text-gray-400 text-sm">Crie trabalhos/entregas por curso. A IA corrige conforme os critérios (nota mínima para aprovação).</p>
        </div>
        <Button onClick={startNew} className="bg-red-600 hover:bg-red-700" data-testid="new-deliverable"><Plus size={16} className="mr-2" />Nova avaliação</Button>
      </div>

      {editing && (
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-5 space-y-3" data-testid="deliverable-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs">Curso</label>
              <select className="w-full bg-zinc-800 border border-zinc-700 rounded-md h-10 px-2 text-white text-sm" value={form.course_id} onChange={e => setForm({ ...form, course_id: e.target.value })} data-testid="deliverable-course">
                <option value="">Selecione...</option>
                {courses.map(c => <option key={c.course_id} value={c.course_id}>{c.course_id} · {c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs">Nota mínima para aprovação</label>
              <Input type="number" step="0.1" min="0" max="10" value={form.pass_score} onChange={e => setForm({ ...form, pass_score: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs">Título</label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="deliverable-title" />
          </div>
          <div>
            <label className="text-gray-400 text-xs">Enunciado / descrição</label>
            <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
          </div>
          <div>
            <label className="text-gray-400 text-xs">Critérios de avaliação (um por linha)</label>
            <Textarea rows={4} value={form.criteria} onChange={e => setForm({ ...form, criteria: e.target.value })} placeholder={"Ex.:\nUso correto de níveis (peso 3)\nModelagem adequada (peso 4)"} className="bg-zinc-800 border-zinc-700 text-white" data-testid="deliverable-criteria" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />Ativa (visível para alunos)</label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}><X size={16} className="mr-2" />Cancelar</Button>
            <Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-deliverable"><Save size={16} className="mr-2" />Salvar</Button>
          </div>
        </div>
      )}

      {loading ? <p className="text-gray-400">Carregando...</p> : items.length === 0 ? (
        <p className="text-gray-500">Nenhuma avaliação de entrega cadastrada.</p>
      ) : (
        <div className="space-y-2">
          {items.map(a => (
            <div key={a.assessment_id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-center justify-between" data-testid={`deliverable-row-${a.assessment_id}`}>
              <div>
                <div className="text-white font-medium">{a.title} {a.is_active === false && <span className="text-xs text-gray-500">(inativa)</span>}</div>
                <div className="text-gray-500 text-xs">{a.course_title} · nota mínima {a.pass_score} · {(a.criteria || []).length} critério(s)</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(a)}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" className="text-red-400 border-red-900/50" onClick={() => remove(a.assessment_id)}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SubmissionsSection />
    </div>
  );
};

export default AdminDeliverables;
